import React from "react";
import globals from '../Globals'
import requests from "../Auth/Requests";
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Box, Typography, Paper, List, ListItem, ListItemText, Button, IconButton, Divider, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, DialogContentText, Tabs, Tab } from '@mui/material';
import { ExpandMore, ExpandLess, Remove, Edit, Check } from '@mui/icons-material';


export default class AdminsPage extends React.Component {
    state = {
        userData: null,
        showDeleted: false,
        foldedUsers: {},
        renamingDevice: null,
        renamingValue: "",
        renamingUserId: null,
        renamingUserValue: "",
        addUserDialogOpen: false,
        newUserName: "",
        newUserEmail: "",
        changeRolesDialogOpen: false,
        rolesEditUser: null,
        roleSelections: {},
        rolesTab: 0,
        locationDialogOpen: false,
        locationDialogContent: ""
    }
    openLocationDialog = (location) => {
        const { query, status, lat, lon, ...rest } = location;
        const cleaned = {
            ip: query,
            ...rest
        };

        this.setState({
            locationDialogOpen: true,
            locationDialogContent: {
                ...cleaned,
                map: { lat, lon }
            }
        });
    }

    closeLocationDialog = () => {
        this.setState({ locationDialogOpen: false });
    }

    toggleUserFold = (userId) => {
        this.setState(prev => ({
            foldedUsers: {
                ...prev.foldedUsers,
                [userId]: !prev.foldedUsers[userId]
            }
        }));
    }

    performAction(description, ...args) {
        const cmd = args.join(" ");

        const key = enqueueSnackbar(`${description}...`, {
            variant: 'info',
            persist: true
        });

        return requests.request("users " + cmd, {})
            .then(response => {
                this.setState({ userData: response });
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, {
                    variant: 'success'
                });
                return response;
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, {
                    variant: 'error'
                });
                throw error;
            });
    }

    getAdminTitleIds = () => {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const allTitleIds = Object.keys(titlesDict);

        // Logged-in user's roles per title
        const myRolesByTitle = globals.loginData?.titles?.roles ?? {};

        const hasAdmin = (raw) => {
            if (!raw) return false;
            // Expected shape: array of strings (e.g. ["admin", "#players"])
            if (Array.isArray(raw)) return raw.includes("admin");
            // Fallback: object-map (e.g. {"admin":true})
            if (typeof raw === "object") return raw["admin"] === true;
            return false;
        };

        return allTitleIds.filter(tid => hasAdmin(myRolesByTitle[tid]));
    }

    renderUsers = () => {
        const { users } = this.state.userData;
        const allowedUsers = users.filter(u => u.allowed);
        const disallowedUsers = users.filter(u => !u.allowed);
        const canAddUsers = globals.loginData?.titles?.hasAdminRole === true;

        users.forEach(user => {
            const hasDevices = user.devices.length > 0;
            const hasPendingAuthorization = user.devices.some(device => !device.authorized);
            const shouldStartFolded = !hasDevices || !hasPendingAuthorization;
            if (this.state.foldedUsers[user.id] === undefined) {
                this.state.foldedUsers[user.id] = shouldStartFolded;
            }
        });

        const pendingAuthUsers = users.filter(user =>
            user.devices.some(device => !device.authorized)
        );

        const renderUserCard = (user, isDisallowed = false) => {
            const hasDevices = user.devices.length > 0;
            const isViewOnly = user.viewOnly === true;
            const isSelf = String(globals.loginData?.id) === String(user.id);

            return (
                <Paper
                    key={user.id}
                    elevation={3}
                    sx={{
                        marginBottom: 3,
                        padding: 2,
                        backgroundColor: isDisallowed ? '#f0f0f0' : 'white'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: this.state.foldedUsers[user.id] ? 0 : 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: isDisallowed ? 0.6 : 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {this.state.renamingUserId === user.id ? (
                                        <>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                value={this.state.renamingUserValue}
                                                onChange={this.handleRenameUserChange}
                                                sx={{ maxWidth: 240 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={this.handleRenameUserSubmit}
                                                sx={{ marginLeft: 1 }}
                                                aria-label="confirm-rename-user"
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h6">
                                                {user.name}
                                            </Typography>
                                            {(!isViewOnly || isSelf) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.handleRenameUser(user.id, user.name)}
                                                    sx={{ marginLeft: 1 }}
                                                    aria-label="rename-user"
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {user.email}
                                </Typography>
                            </Box>
                            {hasDevices ? (
                                <IconButton size="small" onClick={() => this.toggleUserFold(user.id)}>
                                    {this.state.foldedUsers[user.id] ? <ExpandMore /> : <ExpandLess />}
                                </IconButton>
                            ) : (
                                <Remove sx={{ marginLeft: 1 }} />
                            )}
                        </Box>
                        <Box>
                            {!isViewOnly && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="medium"
                                    onClick={() => this.handleChangeRoles(user.id)}
                                    sx={{ minHeight: '40px', marginRight: 1 }}
                                >
                                    Change Roles
                                </Button>
                            )}
                            {!isViewOnly && (
                                <Button
                                    variant="outlined"
                                    color={isDisallowed ? 'success' : 'error'}
                                    size="medium"
                                    onClick={() => isDisallowed ? this.handleRestoreUser(user.id) : this.handleDeleteUser(user.id)}
                                    sx={{ minHeight: '40px' }}
                                >
                                    {isDisallowed ? 'Restore' : 'Delete'}
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {hasDevices && !this.state.foldedUsers[user.id] && (
                        <Box sx={{ opacity: isDisallowed ? 0.6 : 1, marginTop: 1 }}>
                            <List>
                                {user.devices.map((device, index) => (
                                    <ListItem
                                        key={device.id}
                                        divider={index !== user.devices.length - 1}
                                        secondaryAction={
                                            <Box>
                                                {!device.authorized && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="medium"
                                                        onClick={() => this.handleAuthorize(user.id, device.id)}
                                                        sx={{ minHeight: '40px', marginRight: 1 }}
                                                    >
                                                        Authorize
                                                    </Button>
                                                )}
                                                {(!isViewOnly || isSelf) && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="medium"
                                                        onClick={() => this.handleDeleteDevice(user.id, device.id)}
                                                        sx={{ minHeight: '40px' }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    Device:&nbsp;
                                                    {this.state.renamingDevice?.userId === user.id && this.state.renamingDevice?.deviceId === device.id ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                variant="standard"
                                                                value={this.state.renamingValue}
                                                                onChange={this.handleRenameChange}
                                                                sx={{ maxWidth: 200 }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={this.handleRenameSubmit}
                                                                sx={{ marginLeft: 1 }}
                                                            >
                                                                <Check fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {device.name ?? device.id}
                                                            <IconButton
                                                                size="small"
                                                                sx={{ marginLeft: 1 }}
                                                                onClick={() => this.handleRenameDevice(user.id, device.id, device.name)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    Logged In: {device.loggedIn ? 'Yes' : 'No'} | Last Interaction: {device.lastInteraction}
                                                    {device.location && (
                                                        <>
                                                            {' | '}
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                onClick={() => this.openLocationDialog(device.location)}
                                                                sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline', marginLeft: '4px' }}
                                                            >
                                                                {device.location.country}, {device.location.regionName}, {device.location.city}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Paper>
            );
        };

        return (
            <Box sx={{ padding: 2 }}>
                {pendingAuthUsers.length > 0 && (
                    <Typography sx={{ backgroundColor: '#fff3cd', color: '#856404', padding: 2, borderRadius: 1, marginBottom: 2 }}>
                        Pending Authorization: {pendingAuthUsers.map(u => `${u.name} (${u.email})`).join(', ')}
                    </Typography>
                )}
                {allowedUsers.map(user => renderUserCard(user))}
                {canAddUsers && (
                    <Box sx={{ textAlign: 'center', marginY: 2 }}>
                        <Button variant="contained" color="primary" onClick={this.handleAddUser}>
                            Add User
                        </Button>
                    </Box>
                )}
                {disallowedUsers.length > 0 && (
                    <>
                        <Box sx={{ textAlign: 'left', marginBottom: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => this.setState(prev => ({ showDeleted: !prev.showDeleted }))}
                            >
                                {this.state.showDeleted ? 'Hide Deleted Users' : 'Show Deleted Users'}
                            </Button>
                        </Box>
                        {this.state.showDeleted && (
                            <>
                                {disallowedUsers.map(user => renderUserCard(user, true))}
                            </>
                        )}
                    </>
                )}
                <Dialog open={this.state.addUserDialogOpen} onClose={this.handleCancelAddUser}>
                    <DialogTitle>Add New User</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Name"
                            fullWidth
                            value={this.state.newUserName}
                            onChange={(e) => this.setState({ newUserName: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            label="Email"
                            fullWidth
                            value={this.state.newUserEmail}
                            onChange={(e) => this.setState({ newUserEmail: e.target.value })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelAddUser}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmAddUser}>Add</Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={this.state.changeRolesDialogOpen}
                    onClose={this.handleCancelRoles}
                    maxWidth="sm"
                    fullWidth
                >
                    {/* No title */}
                    <DialogContent>
                        {(() => {
                            const titlesDict = globals.loginData?.titles?.titles ?? {};
                            const titleIds = this.getAdminTitleIds();

                            const tabIndex = Math.min(this.state.rolesTab ?? 0, Math.max(titleIds.length - 1, 0));
                            const currentTitleId = titleIds.length > 0 ? titleIds[tabIndex] : null;

                            const currentTitleInfo = currentTitleId ? titlesDict[currentTitleId] : null;
                            const currentTitleName = currentTitleInfo?.title ?? currentTitleInfo?.name ?? currentTitleId ?? "";

                            return (
                                <>
                                    <Tabs
                                        value={tabIndex}
                                        onChange={(e, v) => this.setState({ rolesTab: v })}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{ marginBottom: 1 }}
                                    >
                                        {titleIds.map((titleId) => {
                                            const info = titlesDict[titleId];
                                            const label = info?.title ?? info?.name ?? titleId;
                                            return <Tab key={titleId} label={label} />;
                                        })}
                                    </Tabs>

                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>Role</b></TableCell>
                                                <TableCell align="center" sx={{ width: '80px' }}><b>View</b></TableCell>
                                                <TableCell align="center" sx={{ width: '80px' }}><b>Edit</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(globals.loginData?.titles?.rolesDefinition ?? {}).map((role, index) => (
                                                <TableRow
                                                    key={role}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                                                        '& td': { paddingTop: '4px', paddingBottom: '4px' }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Tooltip
                                                            title={globals.loginData?.titles?.rolesDefinition?.[role]?.description ?? ""}
                                                            placement="top"
                                                        >
                                                            <span>{globals.loginData?.titles?.rolesDefinition?.[role]?.title ?? role}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ width: '80px' }}>
                                                        <Checkbox
                                                            checked={this.state.roleSelections?.[currentTitleId]?.[role]?.view || false}
                                                            onChange={() => this.handleRoleToggle(currentTitleId, role, "view")}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ width: '80px' }}>
                                                        <Checkbox
                                                            checked={this.state.roleSelections?.[currentTitleId]?.[role]?.edit || false}
                                                            onChange={() => this.handleRoleToggle(currentTitleId, role, "edit")}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelRoles}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmRoles}>Save</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={this.state.locationDialogOpen} onClose={this.closeLocationDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Location Details</DialogTitle>
                    <DialogContent>
                        <DialogContentText component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {this.state.locationDialogContent && (() => {
                                const l = this.state.locationDialogContent;
                                return [
                                    `IP: ${l.ip}`,
                                    `Country: ${l.country} (${l.countryCode})`,
                                    `Region: ${l.regionName} (${l.region})`,
                                    `City: ${l.city}`,
                                    `ZIP: ${l.zip}`,
                                    `Time Zone: ${l.timezone}`,
                                    `ISP: ${l.isp}`,
                                    `ORG: ${l.org}`
                                ].join('\n');
                            })()}
                        </DialogContentText>
                        {this.state.locationDialogContent && this.state.locationDialogContent.map && (
                            <Box sx={{ marginTop: 2 }}>
                                <iframe
                                    title="location-map"
                                    width="100%"
                                    height="250"
                                    frameBorder="0"
                                    style={{ border: 0 }}
                                    src={`https://www.google.com/maps?q=${this.state.locationDialogContent.map.lat},${this.state.locationDialogContent.map.lon}&z=14&output=embed`}
                                    allowFullScreen
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeLocationDialog}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    handleAuthorize = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Authorize device for ${user.name}`, "authorize-device", deviceId);
    }

    handleDeleteDevice = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete device for ${user.name}`, "delete-device", deviceId);
    }

    handleDeleteUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete user ${user.name}`, "delete-user", userId);
    }

    handleChangeRoles = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);

        const selectionsByTitle = {};

        const titleIds = this.getAdminTitleIds();

        // user.roles is now Dictionary<titleId, HashSet<string>> (serialized as array or object-map)
        const rolesByTitle = user.roles && typeof user.roles === 'object' ? user.roles : {};

        const roleKeys = Object.keys(globals.loginData?.titles?.rolesDefinition ?? {});

        titleIds.forEach(titleId => {
            const raw = rolesByTitle[titleId];
            const userRoles = Array.isArray(raw)
                ? raw
                : (raw && typeof raw === 'object')
                    ? Object.keys(raw)
                    : [];

            const perTitle = {};
            roleKeys.forEach(role => {
                const edit = userRoles.includes(role);

                // admin role can never be view-only
                const view = (role === "admin")
                    ? edit
                    : (edit || userRoles.includes(`#${role}`));

                perTitle[role] = { view, edit };
            });

            selectionsByTitle[titleId] = perTitle;
        });

        this.setState({
            changeRolesDialogOpen: true,
            rolesEditUser: user,
            roleSelections: selectionsByTitle,
            rolesTab: 0,
            renamingUserId: null,
            renamingUserValue: "",
            renamingDevice: null,
            renamingValue: "",
        });
    }

    handleRestoreUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Restore user ${user.name}`, "restore-user", userId);
    }

    handleAddUser = () => {
        this.setState({ addUserDialogOpen: true });
    }

    handleConfirmAddUser = () => {
        const { newUserName, newUserEmail } = this.state;

        this.performAction(`Add user ${newUserName}`, "add-user", newUserName, newUserEmail)
            .then(() => {
                this.setState(
                    {
                        addUserDialogOpen: false,
                        newUserName: "",
                        newUserEmail: ""
                    },
                    () => {
                        const emailLower = (newUserEmail || "").trim().toLowerCase();
                        const added = (this.state.userData?.users ?? []).find(
                            u => (u.email || "").trim().toLowerCase() === emailLower
                        );
                        if (added) {
                            this.handleChangeRoles(added.id);
                        }
                    }
                );
            });
    }

    handleCancelAddUser = () => {
        this.setState({ addUserDialogOpen: false });
    }

    handleRenameDevice = (userId, deviceId, currentName) => {
        this.setState({
            renamingDevice: { userId, deviceId },
            renamingValue: currentName || ""
        });
    }

    handleRenameChange = (e) => {
        this.setState({ renamingValue: e.target.value });
    }

    handleRenameSubmit = () => {
        const { userId, deviceId } = this.state.renamingDevice;
        const newName = this.state.renamingValue.trim().replace(/\s+/g, "-");
        if (newName) {
            const user = this.state.userData.users.find(u => u.id === userId);
            this.performAction(`Rename device for ${user.name}`, "rename-device", deviceId, newName);
        }
        this.setState({ renamingDevice: null, renamingValue: "" });
    }

    handleRenameUser = (userId, currentName) => {
        this.setState({
            renamingUserId: userId,
            renamingUserValue: currentName || ""
        });
    }

    handleRenameUserChange = (e) => {
        this.setState({ renamingUserValue: e.target.value });
    }

    handleRenameUserSubmit = () => {
        const userId = this.state.renamingUserId;
        const newName = (this.state.renamingUserValue || "").trim().replace(/\s+/g, "-");

        if (userId && newName) {
            const user = this.state.userData.users.find(u => u.id === userId);
            this.performAction(`Rename user ${user?.name ?? userId}`, "rename-user", userId, newName);
        }

        this.setState({ renamingUserId: null, renamingUserValue: "" });
    }

    handleRoleToggle = (titleId, role, type) => {
        if (!titleId) return;
        this.setState(prev => {
            const selections = { ...prev.roleSelections };
            const titleSel = { ...(selections[titleId] ?? {}) };
            const current = { ...(titleSel[role] ?? { view: false, edit: false }) };

            if (role === "admin") {
                // admin role: either full permission (edit) or nothing
                if (type === 'edit') {
                    current.edit = !current.edit;
                    current.view = current.edit;
                } else if (type === 'view') {
                    const next = !current.view;
                    current.view = next;
                    current.edit = next;
                }

                titleSel[role] = current;
                selections[titleId] = titleSel;
                return { roleSelections: selections };
            }

            if (type === 'edit') {
                current.edit = !current.edit;
                if (current.edit) current.view = true;
            }

            if (type === 'view') {
                current.view = !current.view;
                if (!current.view) current.edit = false;
            }

            titleSel[role] = current;
            selections[titleId] = titleSel;
            return { roleSelections: selections };
        });
    }

    handleConfirmRoles = () => {
        const { rolesEditUser, roleSelections } = this.state;

        const titleIds = this.getAdminTitleIds();

        // Build payload: { [titleId]: ["role", "#role", ...] }
        const rolesByTitle = {};
        for (const titleId of titleIds) {
            const perTitle = roleSelections?.[titleId] ?? {};
            const selectedRoles = Object.entries(perTitle).flatMap(([role, perms]) => {
                // admin role can never be view-only
                if (role === "admin") {
                    return perms?.edit ? ["admin"] : [];
                }

                if (perms?.view && perms?.edit) return [role];
                if (perms?.view) return [`#${role}`];
                return [];
            });
            rolesByTitle[titleId] = selectedRoles;
        }

        const description = `Update roles for ${rolesEditUser.name}`;
        const key = enqueueSnackbar(`${description}...`, {
            variant: 'info',
            persist: true
        });

        // Server expects JSON body containing Dictionary<string, HashSet<string>>
        requests.request(`users set-roles ${rolesEditUser.id}`, rolesByTitle)
            .then(response => {
                // Keep UI consistent: update list with latest server state
                this.setState({ userData: response });
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, { variant: 'success' });
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, { variant: 'error' });
            })
            .finally(() => {
                this.setState({ changeRolesDialogOpen: false, rolesEditUser: null, roleSelections: {} });
            });
    }

    handleCancelRoles = () => {
        this.setState({ changeRolesDialogOpen: false, renamingUserId: null, renamingUserValue: "" });
    }

    render() {
        if (this.state.userData == null) {
            return <div>Loading...</div>;
        } else {
            return this.renderUsers();
        }
    }

    componentDidMount() {
        this.performAction("Loading", "list");
    }
}